/* global RequestInit */
import { computed, defineComponent, markRaw, provide } from 'vue';
import { WAITING_UNDO } from '@/client/wrybit/inputs/undoPresentation';
import { createInputHosts } from '@/client/wrybit/shell/inputHosts';
import InputPortal from '@/client/wrybit/shell/InputPortal.vue';
import PresentationHost from '@/client/wrybit/inputs/PresentationHost.vue';
import { INPUT_FOOTER, ROOT_INPUT_FOOTER, useInputPresentation } from '@/client/wrybit/inputs/presentation';
import * as constants from '@/common/constants';
import raw_settings from '@/genfiles/settings.json';
import { vueRoot } from '@/client/components/vueRoot';
import { playerColorClass } from '@/common/utils/utils';
import { getPreferences } from '@/client/utils/PreferencesManager';
import { SoundManager } from '@/client/utils/SoundManager';
import { Phase } from '@/common/Phase';
import { paths } from '@/common/app/paths';
import { statusCode } from '@/common/http/statusCode';
import { isPlayerId } from '@/common/Types';
import { INVALID_REQUEST_ID, INVALID_RUN_ID, UNDO_REVEALED_HIDDEN_INFORMATION } from '@/common/app/AppErrorId';
import { gameDocumentTitle } from '../utils/documentTitle';
import { setFaviconStatus, setFaviconTurnFrame } from '@/client/utils/favicon';
import { trySubmitGameShellInput } from '@/client/shell';
import { redirectToLogin } from '@/client/utils/loginRedirect';
import AppButton from '@/client/components/common/AppButton.vue';
let undoChoiceSequence = 0;
// How often to refresh other players' status during simultaneous phases.
const OTHER_PLAYERS_INTERVAL = 3000;
// Phases where every player makes a choice at the same time.
const SIMULTANEOUS_PHASES = [Phase.INITIALDRAFTING, Phase.DRAFTING, Phase.RESEARCH];
// The spinning ◑◒◐◓ symbol used to indicate it's your turn.
const TURN_SEQUENCE = '◑◒◐◓';
// On a desktop browser the favicon is visible in the tab, so we spin it there
// rather than cluttering the document title. Mobile browsers don't show tab
// favicons, so they keep animating the title instead.
function isDesktopBrowser() {
    return !/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}
const CANNOT_CONTACT_SERVER = 'Unable to reach the server. It may be restarting or down for maintenance.';
const WAITING_FOR_POLL_RETRY_MESSAGE = 'Waiting-for poll failed; retrying.';
export default defineComponent({
    name: 'WaitingFor',
    setup() {
        const footerHost = createInputHosts();
        provide(INPUT_FOOTER, footerHost.target);
        provide(ROOT_INPUT_FOOTER, footerHost.target);
        return { bodyHost: createInputHosts(), footerHost, waitingPresentation: useInputPresentation('waitingFor') };
    },
    provide() {
        return { [WAITING_UNDO]: computed(() => this.undoPresentation) };
    },
    props: {
        playerView: {
            type: Object,
            required: true,
        },
        waitingfor: {
            type: Object,
            default: undefined,
        },
        requestId: {
            type: String,
            default: undefined,
        },
    },
    data() {
        return {
            playersWaitingFor: [],
            suspend: false,
            savedPlayerView: undefined,
            undoChoice: '',
            undoRadioElementName: 'undo-choice-' + undoChoiceSequence++,
            timers: markRaw({ update: undefined, otherPlayers: undefined, title: undefined }),
            animationFrame: 0,
            pollingRevision: 0,
            active: true,
        };
    },
    components: {
        AppButton,
        InputPortal,
        PresentationHost,
    },
    watch: {
        waitingfor() {
            if (!getPreferences().experimental_layout) {
                this.undoChoice = '';
            }
        },
        playerView() {
            this.startWaiting();
        },
    },
    methods: {
        startWaiting() {
            this.pollingRevision++;
            window.clearTimeout(this.timers.update);
            window.clearTimeout(this.timers.otherPlayers);
            window.clearInterval(this.timers.title);
            if (this.waitingfor === undefined || this.waitingfor.optional) {
                this.waitForUpdate();
            }
            else if (this.playerView.players.length > 1 && SIMULTANEOUS_PHASES.includes(this.playerView.game.phase)) {
                this.watchOtherPlayers();
            }
            if (this.playerView.players.length > 1 && this.waitingfor !== undefined && !this.waitingfor.optional) {
                this.timers.title = window.setInterval(() => this.animateTitle(), 1000);
            }
        },
        getPlayerName(color) {
            const player = this.playerView.players.find((p) => p.color === color);
            return player ? player.name : color;
        },
        animateTitle() {
            if (!getPreferences().animated_title) {
                return;
            }
            this.animationFrame = (this.animationFrame + 1) % TURN_SEQUENCE.length;
            const experimental = getPreferences().experimental_ui;
            // The favicon annotation is an experimental feature.
            if (experimental) {
                setFaviconTurnFrame(this.animationFrame);
            }
            // Existing behavior spins the symbol in the document title. With
            // experimental UI on a desktop browser we show it only in the tab favicon
            // instead; otherwise keep animating the title.
            if (!(experimental && isDesktopBrowser())) {
                document.title = TURN_SEQUENCE[this.animationFrame] + ' ' + gameDocumentTitle(this.playerView.game);
            }
        },
        onsave(out) {
            // Default-off bridge: without a server-issued v2 bootstrap this returns
            // false and the legacy POST below stays byte-for-byte unchanged.
            if (trySubmitGameShellInput(this.playerView, out)) {
                return;
            }
            this.fetchPlayerInput(paths.PLAYER_INPUT + '?id=' + this.playerView.id, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ runId: this.playerView.runId, promptId: this.waitingfor?.promptId, requestId: this.requestId, ...out }),
            });
        },
        stepBack() {
            this.fetchPlayerInput(paths.RESET + '?id=' + this.playerView.id + '&mode=step', { method: 'GET' });
        },
        undoResearchPurchase() {
            this.fetchPlayerInput(paths.RESET + '?id=' + this.playerView.id + '&mode=research', { method: 'GET' });
        },
        reset() {
            this.fetchPlayerInput(paths.RESET + '?id=' + this.playerView.id, { method: 'GET' });
        },
        undoSelected() {
            if (this.undoChoice === 'step') {
                this.stepBack();
            }
            else if (this.undoChoice === 'action') {
                this.reset();
            }
        },
        runPresentationUndo(mode) {
            if (!this.active || this.waitingPresentation === undefined || this.presentationBinding === undefined ||
                !this.undoPresentation.actions.some((action) => action.mode === mode)) {
                return;
            }
            if (mode === 'research') {
                this.undoResearchPurchase();
            }
            else if (mode === 'step') {
                this.stepBack();
            }
            else {
                this.reset();
            }
        },
        onsavePlayerInput(out) {
            if (this.isStepBackResponse(out)) {
                this.stepBack();
                return;
            }
            this.onsave(out);
        },
        fetchPlayerInput(url, options) {
            if (!this.active) {
                return;
            }
            const root = vueRoot(this);
            if (root.isServerSideRequestInProgress) {
                console.warn('Server request in progress');
                return;
            }
            root.isServerSideRequestInProgress = true;
            root.refreshRevision++;
            fetch(url, options)
                .then(async (response) => {
                if (!this.active) {
                    return;
                }
                if (response.ok) {
                    const model = await response.json();
                    if (this.active) {
                        this.updatePlayerView(model);
                    }
                    return;
                }
                const showAlert = vueRoot(this).showAlert;
                if (response.status === statusCode.unauthorized || response.status === statusCode.forbidden) {
                    const message = response.status === statusCode.unauthorized ?
                        'Sign in with Discord to play this game.' :
                        'This seat belongs to another Discord account. Sign in with the right account.';
                    showAlert('Sign in required', message, () => redirectToLogin());
                    return;
                }
                if (response.status === statusCode.badRequest) {
                    const resp = await this.readAppError(response);
                    if (resp.id === UNDO_REVEALED_HIDDEN_INFORMATION &&
                        !url.includes('confirmHiddenInformation=true')) {
                        if (window.confirm(resp.message)) {
                            const separator = url.includes('?') ? '&' : '?';
                            window.setTimeout(() => {
                                this.fetchPlayerInput(url + separator + 'confirmHiddenInformation=true', options);
                            }, 0);
                        }
                        return;
                    }
                    let cb = () => {
                        void root.updatePlayer();
                    };
                    if (resp.id === INVALID_RUN_ID || resp.id === INVALID_REQUEST_ID) {
                        cb = () => setTimeout(() => window.location.reload(), 100);
                    }
                    showAlert('Error with input', resp.message, cb);
                }
                else {
                    showAlert('Error processing response', 'Unexpected response from server. Please try again.');
                    console.error(response.statusText);
                }
            })
                .catch((e) => {
                if (!this.active) {
                    return;
                }
                root.showAlert('Error sending input,', CANNOT_CONTACT_SERVER);
                console.error(e);
            })
                .finally(() => {
                root.isServerSideRequestInProgress = false;
                if (this.active) {
                    this.startWaiting();
                }
            });
        },
        async readAppError(response) {
            try {
                return await response.clone().json();
            }
            catch (_err) {
                return {
                    id: undefined,
                    message: await response.text(),
                };
            }
        },
        updatePlayerView(playerView) {
            if (!this.active) {
                return;
            }
            if (this.suspend === false) {
                const root = vueRoot(this);
                if (typeof root.applyPlayerView === 'function') {
                    root.applyPlayerView(playerView, true);
                }
                else {
                    root.playerView = playerView;
                    root.playerkey++;
                }
                root.screen = 'player-home';
                if (playerView?.game.phase === 'end' && window.location.pathname !== paths.THE_END) {
                    window.location = window.location;
                }
                this.savedPlayerView = undefined;
            }
            else {
                this.savedPlayerView = playerView;
            }
        },
        waitForUpdate() {
            const vueApp = this;
            const root = vueRoot(this);
            const revision = this.pollingRevision;
            const current = () => this.active && revision === this.pollingRevision;
            clearTimeout(this.timers.update);
            const retryAfterWaitingForPollError = (reason) => {
                if (!current()) {
                    return;
                }
                console.warn(`${WAITING_FOR_POLL_RETRY_MESSAGE} ${reason}`);
                vueApp.waitForUpdate();
            };
            const askForUpdate = () => {
                if (!current()) {
                    return;
                }
                const xhr = new XMLHttpRequest();
                xhr.open('GET', paths.API_WAITING_FOR + window.location.search + '&gameAge=' + this.playerView.game.gameAge + '&undoCount=' + this.playerView.game.undoCount);
                xhr.onerror = function () {
                    retryAfterWaitingForPollError(CANNOT_CONTACT_SERVER);
                };
                xhr.onload = async () => {
                    if (!current()) {
                        return;
                    }
                    if (xhr.status === statusCode.ok) {
                        const result = xhr.response;
                        this.playersWaitingFor = result.waitingFor;
                        if (result.result === 'GO') {
                            // Will only apply to player, not spectator.
                            const updated = root.updatePlayer();
                            this.notify();
                            // When the fetch works, root.playerView changes and the playerView watcher
                            // calls startWaiting(), which decides whether to keep polling. When it fails
                            // nothing changes, so poll again here.
                            if (!await updated) {
                                vueApp.waitForUpdate();
                            }
                            // We don't need to wait anymore - it's our turn
                            return;
                        }
                        else if (result.result === 'REFRESH') {
                            // Something changed, let's refresh UI
                            if (isPlayerId(this.playerView.id)) {
                                if (!await root.updatePlayer()) {
                                    vueApp.waitForUpdate();
                                }
                            }
                            else if (!await root.updateSpectator()) {
                                vueApp.waitForUpdate();
                            }
                            return;
                        }
                        vueApp.waitForUpdate();
                    }
                    else {
                        retryAfterWaitingForPollError(`Received unexpected response from server (${xhr.status}).`);
                    }
                };
                xhr.responseType = 'json';
                xhr.send();
            };
            this.timers.update = window.setTimeout(askForUpdate, raw_settings.waitingForTimeout);
        },
        /**
         * While this player makes a simultaneous choice (e.g. drafting), keep the other
         * players' status current without redrawing the choice in progress.
         */
        watchOtherPlayers() {
            const playerView = this.playerView;
            window.clearTimeout(this.timers.otherPlayers);
            // Schedule the next poll only after this one finishes, so responses can't arrive out of order.
            const timer = window.setTimeout(async () => {
                try {
                    const response = await fetch(paths.API_PLAYER + window.location.search);
                    if (response.ok) {
                        const latest = await response.json();
                        if (!this.active || this.timers.otherPlayers !== timer) {
                            return;
                        }
                        playerView.players = latest.players;
                    }
                    else {
                        console.warn('Unable to update other players', response.status, response.statusText);
                        // Client errors (e.g. the game no longer exists) won't recover, so stop polling.
                        if (response.status < 500) {
                            return;
                        }
                    }
                }
                catch (e) {
                    console.warn('Unable to update other players', e);
                }
                // Stop if the component unmounted or another poll started while this one was in flight.
                if (this.active && this.timers.otherPlayers === timer) {
                    this.watchOtherPlayers();
                }
            }, OTHER_PLAYERS_INTERVAL);
            this.timers.otherPlayers = timer;
        },
        async notify() {
            if (getPreferences().enable_sounds) {
                SoundManager.playActivePlayerSound();
            }
            const notificationOptions = {
                icon: 'favicon.ico',
                body: 'It\'s your turn!',
            };
            const notificationTitle = constants.APP_NAME;
            if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
                try {
                    const permission = await Notification.requestPermission();
                    if (permission !== 'granted') {
                        return;
                    }
                }
                catch (err) {
                    console.warn('Failed to request notification permission', err);
                    return;
                }
            }
            try {
                if (typeof Notification === 'undefined') {
                    throw new Error('Notification API unavailable');
                }
                new Notification(notificationTitle, notificationOptions);
            }
            catch (e) {
                // ok so the native Notification doesn't work which will happen
                // try to use the service worker
                if (!window.isSecureContext || !navigator.serviceWorker) {
                    return;
                }
                navigator.serviceWorker.ready.then((registration) => {
                    registration.showNotification(notificationTitle, notificationOptions);
                }).catch((err) => {
                    // avoid promise going uncaught
                    console.warn('Failed to display notification with serviceWorker', err);
                });
            }
        },
        updateSuspend() {
            if (this.suspend === false && this.savedPlayerView !== undefined) {
                this.updatePlayerView(this.savedPlayerView);
            }
        },
        showRefresh() {
            return this.suspend === true && this.savedPlayerView !== undefined;
        },
        showResearchPurchaseUndo() {
            return this.playerView.canUndoResearchPurchase === true && this.playerView.game.phase === Phase.RESEARCH;
        },
        showStepBack() {
            return this.playerView.game.gameOptions?.undoStepOption === true &&
                this.playerView.canStepBack === true &&
                this.waitingfor !== undefined &&
                this.playerView.thisPlayer?.isActive === true;
        },
        showStepBackOption() {
            return this.isMainActionPrompt() && this.showStepBack();
        },
        showUndoAction() {
            const phase = this.playerView.game.phase;
            const supportedPhase = phase === Phase.ACTION || phase === Phase.PRELUDES || phase === Phase.CEOS;
            const enabled = this.playerView.players.length === 1 ||
                this.playerView.game.gameOptions?.undoOption === true ||
                this.playerView.game.gameOptions?.undoStepOption === true;
            return supportedPhase && enabled && this.playerView.thisPlayer?.isActive === true;
        },
        showUndoFooter() {
            return !this.isMainActionPrompt() && (this.showUndoAction() || this.showStepBack());
        },
        playerinputWithStepBack() {
            const playerinput = this.waitingfor;
            if (playerinput === undefined) {
                throw new Error('Missing player input');
            }
            if (!this.showStepBackOption() || playerinput.type !== 'or') {
                return playerinput;
            }
            return {
                ...playerinput,
                options: [...playerinput.options, { type: 'option', title: 'Undo one step (experimental)', buttonLabel: 'Undo' }],
            };
        },
        isStepBackResponse(out) {
            const playerinput = this.waitingfor;
            return this.showStepBackOption() &&
                playerinput?.type === 'or' &&
                out.type === 'or' &&
                out.index === playerinput.options.length &&
                out.response.type === 'option';
        },
        isMainActionPrompt() {
            return this.waitingfor?.type === 'or' && this.waitingfor.buttonLabel === 'Take action';
        },
        playerName(color) {
            const player = this.playerView.players.find((p) => p.color === color);
            return player?.name ?? '';
        },
    },
    mounted() {
        document.title = gameDocumentTitle(this.playerView.game);
        if (getPreferences().experimental_ui) {
            setFaviconStatus(this.waitingfor !== undefined ? 'turn' : 'idle');
        }
        this.startWaiting();
    },
    beforeUnmount() {
        this.active = false;
        window.clearTimeout(this.timers.update);
        window.clearTimeout(this.timers.otherPlayers);
        window.clearInterval(this.timers.title);
    },
    computed: {
        undoPresentation() {
            const actions = [];
            if (this.showResearchPurchaseUndo()) {
                actions.push({ mode: 'research', title: 'Undo card purchase (experimental)' });
            }
            if (this.showUndoFooter()) {
                if (this.showUndoAction()) {
                    actions.push({ mode: 'action', title: 'Undo action' });
                }
                if (this.showStepBack()) {
                    actions.push({ mode: 'step', title: 'Undo one step (experimental)' });
                }
            }
            return { actions, run: this.runPresentationUndo };
        },
        presentationBinding() {
            if (this.waitingfor === undefined || this.playerView.thisPlayer === undefined) {
                return undefined;
            }
            // Pending inputs belong to our actor view; spectator views have no input.
            return { playerView: this.playerView, playerinput: this.waitingfor,
                bodyHost: this.bodyHost, footerHost: this.footerHost };
        },
        Phase() {
            return Phase;
        },
        preferences() {
            return getPreferences;
        },
        playerColorClass() {
            return playerColorClass;
        },
    },
});
